import React, { useState, useEffect } from 'react';
import { Table, Space, Typography, Col, Input } from 'antd';
import { SearchOutlined } from "@ant-design/icons";
import TableWithLoadingWrapper from '../../../components/tables/tableWithLoadingWrapper';
import { toPrice } from '../../../helpers/stringFormat';
import HeaderBox from "../../../components/generic/arpariBoxes/headerBox";
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import ExportBankExposureAccountModal from './tempComponents/bankExposure/tableDropdown/ExportBankExposureAccountModal';
const { Text } = Typography;


const bankExposureDataFormat = {
	institutionName: "",
	balance: 0,
	entities: [
		{ entityName: "", balance: 0 },
	],
	entitiesOver250k: [],
	entitiesCount: 0,
	entitiesOver250kCount: 0,
	key: ""
};

function BankExposure(props) {
	const [expandedKeys, setExpandedKeys] = useState([]);
	const [bankAccounts, setBankAccounts] = useState(props.tableData || [])
	const [originalBankAccounts, setOriginalBankAccounts] = useState(props.tableData || []);
	const [csvModalVisible, setCsvModalVisible] = useState(false);

	useEffect(() => {
		const accounts = props.tableData.map((account) => {
			return {
				...account,
				key: account.key,
			}
		});
		setBankAccounts(accounts)
		setOriginalBankAccounts(accounts)

	}, [props.tableData]);

	const tableHeader = [
		{
			title: 'Institution',
			dataIndex: 'institutionName',
			key: 'institutionName',
			width: '25%',
			sorter: {
				compare: (a, b) => a.institutionName.localeCompare(b.institutionName),
			},
		},
		{
			title: 'No. Entities',
			dataIndex: 'entitiesCount',
			key: 'entitiesCount',
			width: '25%',
			sorter: {
				compare: (a, b) => a.entitiesCount - b.entitiesCount,
			},
		},
		{
			title: "Entities > $250k",
			dataIndex: 'entitiesOver250kCount',
			key: 'entitiesOver250kCount',
			width: '25%',
			sorter: {
				compare: (a, b) => a.entitiesOver250kCount - b.entitiesOver250kCount,
			},
			render(_, record) {
				return <span>{record.entitiesOver250kCount > 0 ? "Yes" : "No"}</span>;
			}
		},
		{
			title: 'Balance',
			dataIndex: 'balance',
			key: 'balance',
			width: '25%',
			sorter: {
				compare: (a, b) => a.balance - b.balance,
			},
			render: (text, record) => {
				return (
					<span>{toPrice(record.balance)}</span>
				)
			}
		},
	];

	const nestedTableHeader = [
		{
			title: 'Entity',
			dataIndex: 'entityName',
			key: 'entityName',
			defaultSortOrder: 'ascend',
			width: '25%',
		},
		{
			title: 'No. Bank Accounts',
			width: '25%',
			render: (_, record) => {
				return <span>{record.bankAccounts.length}</span>
			}
		},
		{
			title: '',
			width: '25%',
			render: (_, record) => {
				return <span>{record.balance > 250000 ? "Yes" : "No"}</span>
			}
		},
		{
			title: '',
			dataIndex: 'balance',
			key: 'balance',
			width: '25%',
			render: (_, record) => {
				return (
					<span>{toPrice(record.balance)}</span>
				)
			},
		}
	];

	const bankAccountsTableHeader = [
		{
			title: 'Bank Account',
			dataIndex: 'account',
			key: 'account',
			defaultSortOrder: 'ascend',
			width: '75%',
			render: (_, record) => {
				return <span>{record.account.account_description}</span>
			}
		},
		{
			title: '',
			dataIndex: 'balance',
			key: 'balance',
			width: '25%',
			render: (_, record) => {
				return <span>{record.account.current_balance}</span>
			},
		}
	];

	const entityExpandRowRender = (record, expandedKeys, setExpandedKeys) => {
		const handleExpand = (expanded, recordEnt) => {
			const key = recordEnt.key;
			setExpandedKeys((prevKeys) => {
				if (expanded) {
					return [...prevKeys, key];
				} else {
					return prevKeys.filter((k) => k !== key);
				}
			});
		};
		return (
			<Table
				columns={nestedTableHeader}
				dataSource={record.entities}
				pagination={false}
				size="small"
				expandable={{
					expandedRowRender: (recordEnt) => {
						return (
							<Table
								columns={bankAccountsTableHeader}
								dataSource={recordEnt.bankAccounts}
								pagination={false}
								size="small"
							/>
						);
					},
					expandIconColumnIndex: 4,
					expandedRowKeys: expandedKeys,
					onExpand: handleExpand,

				}}
			/>
		);
	}

	const handleSearch = (e) => {
		const searchText = e.target.value.trim();

		if (searchText === "") {
			setBankAccounts(originalBankAccounts); // Reset to full data
		} else {
			const filteredData = originalBankAccounts.filter((item) =>
				item.institutionName.toLowerCase().includes(searchText.toLowerCase())
			);
			setBankAccounts(filteredData);
		}
	};

	return (
		<>
			<Space direction="vertical" style={{ width: "100%", marginTop: '8px' }}>
				<Grid container spacing={2} alignItems="flex-end">
					<Grid item xs={6}>
						<Input
							disabled={props.loadingConnectedAccountsSlow}
							placeholder="Search"
							prefix={<SearchOutlined />}
							onChange={handleSearch}
						/>
					</Grid>
					<Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'row', justifyItems: 'center', gap: '8px' }}>
						<Button
							variant="contained"
							disabled={(!props.institutions || props.institutions.length === 0)}
							onClick={(!props.institutions || props.institutions.length === 0) ? undefined : () => setCsvModalVisible(true)}
							sx={{ fontSize: '12px', boxShadow: 'none' }}
						>
							Export Data
						</Button>
						<ExportBankExposureAccountModal
							tableData={props.institutions}
							csvModalVisible={csvModalVisible}
							handleCancelModal={() => setCsvModalVisible(false)}
						/>
					</Grid>
				</Grid>
				<Text type="secondary" italic>This table only includes accounts assigned to your dashboard.</Text>

				<TableWithLoadingWrapper
					rowKey={(record) => record.key}
					columns={tableHeader}
					dataSource={bankAccounts}
					pagination={false}
					expandable={{
						expandedRowRender: (record) => {
							return entityExpandRowRender(record, expandedKeys, setExpandedKeys);
						},
						rowExpandable: (record) => record.entitiesCount > 0,
						expandIconColumnIndex: 4,
					}}
					dataFormat={bankExposureDataFormat}
					numLoadingEntries={2}
					loading={props.loadingConnectedAccounts}
					loadingEntriesTop={true}
				/>

			</Space>
		</>
	)
}

export default BankExposure